import { useState, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  Tabs,
  Tab,
  Grid,
  Tooltip,
  InputAdornment,
  Chip,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Close,
  Search,
  Image as ImageIcon,
} from '@mui/icons-material';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from '../common/components/LocalizationProvider';

// Mapeamento de ícones Lucide por categoria (sincronizado com changelog-poi.xml)
const LUCIDE_CATEGORIES = {
  Clientes: ['Building2', 'User', 'Users', 'Briefcase', 'Store', 'Home'],
  Bases: ['Warehouse', 'Factory', 'Building', 'Landmark', 'Castle', 'School'],
  Logística: ['Package', 'Truck', 'Container', 'Boxes', 'PackageCheck', 'PackageX', 'Forklift'],
  Automotivo: ['Car', 'CarFront', 'Bike', 'Bus', 'Ambulance', 'Fuel', 'ParkingCircle', 'Wrench', 'CircleGauge'],
  Transporte: ['Ship', 'Plane', 'Train', 'Tractor', 'Sailboat', 'Cable', 'TramFront'],
  Segurança: ['Shield', 'ShieldAlert', 'ShieldCheck', 'Camera', 'Siren', 'Lock', 'Eye', 'Bell'],
  Paradas: ['Coffee', 'UtensilsCrossed', 'Bed', 'Bath', 'Wifi', 'Tv', 'Sofa', 'Armchair'],
  Serviços: ['Stethoscope', 'GraduationCap', 'Gavel', 'Banknote', 'ShoppingCart', 'Scissors', 'Brush'],
  Comércio: ['ShoppingBag', 'Receipt', 'CreditCard', 'Wallet', 'PiggyBank', 'TrendingUp'],
  Agro: ['Wheat', 'Leaf', 'Trees', 'Flower2', 'Bug', 'Sun'],
  Construção: ['HardHat', 'Hammer', 'Ruler', 'PaintBucket', 'Cone'],
  Geral: ['MapPin', 'MapPinned', 'Navigation', 'Compass', 'Flag', 'Star', 'Heart', 'Bookmark', 'Tag', 'Circle'],
};

// Cores sugeridas por categoria
const CATEGORY_COLORS = {
  Clientes: '#3B82F6',
  Bases: '#6366F1',
  Logística: '#F59E0B',
  Automotivo: '#EF4444',
  Transporte: '#06B6D4',
  Segurança: '#DC2626',
  Paradas: '#10B981',
  Serviços: '#8B5CF6',
  Comércio: '#EC4899',
  Agro: '#22C55E',
  Construção: '#F97316',
  Geral: '#6B7280',
};

const PoiIconSelector = ({
  open,
  onClose,
  onSelect,
  selectedIconId,
  icons = [], // Lista de ícones do banco (PNG)
  loading = false,
}) => {
  const t = useTranslation();
  const [tabValue, setTabValue] = useState(0); // 0 = Lucide, 1 = PNG
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Filtrar ícones Lucide
  const filteredLucideIcons = useMemo(() => {
    const categories = selectedCategory
      ? { [selectedCategory]: LUCIDE_CATEGORIES[selectedCategory] }
      : LUCIDE_CATEGORIES;

    const result = {};
    Object.entries(categories).forEach(([category, iconNames]) => {
      const filtered = iconNames.filter((name) =>
        name.toLowerCase().includes(searchQuery.toLowerCase())
        || category.toLowerCase().includes(searchQuery.toLowerCase()));
      if (filtered.length > 0) {
        result[category] = filtered;
      }
    });
    return result;
  }, [searchQuery, selectedCategory]);

  // Filtrar ícones PNG do banco (apenas com path válido)
  const filteredPngIcons = useMemo(() => {
    if (!icons.length) return [];
    return icons.filter((icon) =>
      icon.iconType === 'png'
      && icon.path // Garantir que path não seja vazio
      && (icon.name?.toLowerCase().includes(searchQuery.toLowerCase())
        || icon.category?.toLowerCase().includes(searchQuery.toLowerCase())));
  }, [icons, searchQuery]);

  // Agrupar ícones PNG por categoria
  const groupedPngIcons = useMemo(() => {
    const groups = {};
    filteredPngIcons.forEach((icon) => {
      const category = icon.category || 'Geral';
      if (!groups[category]) groups[category] = [];
      groups[category].push(icon);
    });
    return groups;
  }, [filteredPngIcons]);

  const handleLucideSelect = (iconCode, category) => {
    onSelect({
      iconType: 'lucide',
      iconCode,
      category,
      suggestedColor: CATEGORY_COLORS[category] || '#6B7280',
    });
    onClose();
  };

  const handlePngSelect = (icon) => {
    onSelect({
      id: icon.id,
      iconType: 'png',
      path: icon.path,
      name: icon.name,
      category: icon.category,
      suggestedColor: icon.suggestedColor || '#6B7280',
    });
    onClose();
  };

  const renderLucideIcon = (iconName, size = 24) => {
    const IconComponent = LucideIcons[iconName];
    if (!IconComponent) return null;
    return <IconComponent size={size} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh', maxHeight: 700 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Box component="span" sx={{ flex: 1, fontWeight: 500 }}>
          {t('poiSelectIcon') || 'Selecionar Ícone'}
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {/* Tabs: Lucide vs PNG */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab
              label={t('poiIconsLucide') || 'Ícones Lucide'}
              icon={<LucideIcons.Sparkles size={18} />}
              iconPosition="start"
            />
            <Tab
              label={t('poiIconsPng') || 'Ícones PNG'}
              icon={<ImageIcon fontSize="small" />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Busca e Filtros */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('sharedSearch') || 'Buscar...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />

          {tabValue === 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
              <Chip
                label={t('sharedAll') || 'Todas'}
                size="small"
                variant={selectedCategory === null ? 'filled' : 'outlined'}
                onClick={() => setSelectedCategory(null)}
                color={selectedCategory === null ? 'primary' : 'default'}
              />
              {Object.keys(LUCIDE_CATEGORIES).map((category) => (
                <Chip
                  key={category}
                  label={category}
                  size="small"
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCategory(category)}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  sx={{
                    borderColor: CATEGORY_COLORS[category],
                    '&.MuiChip-filled': {
                      backgroundColor: CATEGORY_COLORS[category],
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Conteúdo */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : tabValue === 0 ? (
            /* Ícones Lucide */
            Object.entries(filteredLucideIcons).map(([category, iconNames]) => (
              <Box key={category} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: CATEGORY_COLORS[category],
                    }}
                  />
                  {category}
                  <Chip label={iconNames.length} size="small" variant="outlined" />
                </Typography>
                <Grid container spacing={1}>
                  {iconNames.map((iconName) => (
                    <Grid key={iconName}>
                      <Tooltip title={iconName} placement="top">
                        <Paper
                          elevation={0}
                          onClick={() => handleLucideSelect(iconName, category)}
                          sx={{
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            transition: 'all 0.2s',
                            color: CATEGORY_COLORS[category],
                            '&:hover': {
                              borderColor: CATEGORY_COLORS[category],
                              backgroundColor: `${CATEGORY_COLORS[category]}10`,
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          {renderLucideIcon(iconName, 24)}
                        </Paper>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))
          ) : (
            /* Ícones PNG */
            Object.keys(groupedPngIcons).length > 0 ? (
              Object.entries(groupedPngIcons).map(([category, categoryIcons]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {category}
                    <Chip label={categoryIcons.length} size="small" variant="outlined" sx={{ ml: 1 }} />
                  </Typography>
                  <Grid container spacing={1}>
                    {categoryIcons.map((icon) => (
                      <Grid key={icon.id}>
                        <Tooltip title={icon.name} placement="top">
                          <Paper
                            elevation={0}
                            onClick={() => handlePngSelect(icon)}
                            sx={{
                              width: 48,
                              height: 48,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: selectedIconId === icon.id ? 'primary.main' : 'divider',
                              borderRadius: 1,
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: 'primary.main',
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            <img
                              src={icon.path?.startsWith('/') ? icon.path : `/images/${icon.path}`}
                              alt={icon.name}
                              style={{ width: 32, height: 32, objectFit: 'contain' }}
                            />
                          </Paper>
                        </Tooltip>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography>
                  {t('poiNoIconsFound') || 'Nenhum ícone PNG encontrado'}
                </Typography>
              </Box>
            )
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PoiIconSelector;
